import React, { useState, useEffect } from 'react';
import './App.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Vote {
  id: number;
  name: string;
  is_attending: boolean;
  guests_count: number;
  created_at: string;
  guest_names?: string;
}

interface Stats {
  attending: number;
  notAttending: number;
  totalGuests: number;
  totalPeople: number;
}

function App() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    isAttending: false,
    guestsCount: 0,
    guestNames: [] as { firstName: string; lastName: string }[]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState({
    firstName: '',
    lastName: '',
    isAttending: false,
    guestsCount: 0,
    isUpdate: false
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchVotes = async () => {
    try {
      const response = await fetch('/api/votes');
      const data = await response.json();
      setVotes(data);
    } catch (err) {
      setError('Failed to fetch votes');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError('Failed to fetch statistics');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const guestNames = formData.isAttending 
        ? formData.guestNames
            .filter(guest => guest.firstName.trim() && guest.lastName.trim())
            .map(guest => `${guest.firstName.trim()} ${guest.lastName.trim()}`)
        : [];

      const response = await fetch('/api/votes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          isAttending: formData.isAttending,
          guestsCount: formData.isAttending ? formData.guestsCount : 0,
          guestNames
        }),
      });

      const result = await response.json();

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        isAttending: false,
        guestsCount: 0,
        guestNames: [] as { firstName: string; lastName: string }[]
      });

      await fetchStats();
      
      // Show confirmation
      setConfirmationData({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        isAttending: formData.isAttending,
        guestsCount: formData.guestsCount,
        isUpdate: result.isUpdate || false
      });
      setShowConfirmation(true);
    } catch (err) {
      setError('Failed to submit vote');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestsCountChange = (count: number) => {
    setFormData(prev => ({
      ...prev,
      guestsCount: count,
      guestNames: Array(count).fill('').map((_, i) => prev.guestNames[i] || { firstName: '', lastName: '' })
    }));
  };

  const handleGuestNameChange = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setFormData(prev => ({
      ...prev,
      guestNames: prev.guestNames.map((guest, i) => 
        i === index 
          ? { ...guest, [field]: value }
          : guest
      )
    }));
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === '#partydesjahres120!') {
      setIsAuthenticated(true);
      fetchVotes();
    } else {
      setError('Falsches Passwort');
    }
  };

  return (
    <div className="App">
      <div className="container">
        <header className="header">
          <h2>Geburtstag</h2>
          <h2>Lisann - Sabine - Eileen</h2>
        <br></br>
          <p>Bist du dabei?</p>
        </header>

        {showConfirmation ? (
          // Bestätigungsseite
          <div className="confirmation-section">
            <div className="confirmation-card">
              <div className="confirmation-icon">
                {confirmationData.isAttending ? '🎉' : '😔'}
              </div>
              <h2>
                {confirmationData.isAttending 
                  ? (confirmationData.isUpdate
                      ? `Schön, dass du doch kommen kannst, ${confirmationData.firstName} ${confirmationData.lastName}!`
                      : (confirmationData.guestsCount > 0 
                          ? `Super, ${confirmationData.firstName} ${confirmationData.lastName}! Ihr seid dabei!` 
                          : `Super, ${confirmationData.firstName} ${confirmationData.lastName}! Du bist dabei!`))
                  : (confirmationData.isUpdate
                      ? `Schade, ${confirmationData.firstName} ${confirmationData.lastName}, dass du doch nicht kommen kannst!`
                      : `Schade, ${confirmationData.firstName} ${confirmationData.lastName}!`)
                }
              </h2>
              <p className="confirmation-message">
                {confirmationData.isAttending 
                  ? (confirmationData.isUpdate
                      ? 'Wir haben deine Zusage aktualisiert und freuen uns auf dich! 🎂'
                      : `Wir freuen uns auf dich${confirmationData.guestsCount > 0 ? ' und deine Gäste' : ''}! 🎂`)
                  : (confirmationData.isUpdate
                      ? 'Wir haben deine Absage aktualisiert. Vielleicht beim nächsten Mal!'
                      : 'Wir hoffen, wir sehen dich bald bei einer anderen Gelegenheit!')
                }
              </p>
              
              {confirmationData.isAttending && (
              <div className="event-info">
                <h3>📍 Event-Informationen</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">📅 Datum:</span>
                    <span className="info-value">Samstag, 21. November 2026</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">⏰ Uhrzeit:</span>
                    <span className="info-value">18:30 Uhr</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">🏠 Ort:</span>
                    <span className="info-value">Kulturschmiede Greven<br/>Friedrich-Ebert-Straße 3<br/>48268 Greven</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">🎁 Geschenke:</span>
                    <span className="info-value">Deine Anwesenheit ist das größte Geschenk!</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">🚗 Parken:</span>
                    <span className="info-value">Parken könnt ihr ganz entspannt am Busbahnhof oder auf dem Wilhelmplatz.<br/>Bitte nicht beim Hagebaumarkt oder Aldi parken – dort könnte es teuer werden</span>
                  </div>
                </div>
              </div>
            )}
              
              <div className="confirmation-actions">
                <button 
                  type="button" 
                  onClick={() => setShowConfirmation(false)}
                  className="back-btn"
                >
                  Zurück zur Startseite
                </button>
                {showAdmin && (
                  <button 
                    type="button" 
                    onClick={() => {
                      setShowConfirmation(false);
                      setShowAdmin(true);
                    }}
                    className="admin-btn"
                  >
                    🔐 Admin
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : showAdmin ? (
          // Admin Seite
          <div className="admin-section">
            {!isAuthenticated ? (
              <div className="login-form">
                <h2>Admin Login</h2>
                <form onSubmit={handleLogin}>
                  <div className="form-group">
                    <label htmlFor="password">Passwort</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Passwort eingeben"
                      required
                    />
                  </div>
                  {error && <div className="error">{error}</div>}
                  <button type="submit" className="submit-btn">
                    Login
                  </button>
                </form>
                <button 
                  type="button" 
                  onClick={() => setShowAdmin(false)}
                  className="back-btn"
                >
                  Zurück
                </button>
              </div>
            ) : (
              <div className="admin-dashboard">
                <h2>Admin Dashboard</h2>
                {stats && (
                  <div className="admin-stats">
                    <div className="stat-item">
                      <span className="stat-number">{stats.attending}</span>
                      <span className="stat-label">Dabei</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.notAttending}</span>
                      <span className="stat-label">Nicht dabei</span>
                    </div>
                    {/* <div className="stat-item">
                      <span className="stat-number">{stats.totalGuests}</span>
                      <span className="stat-label">Zusätzliche Gäste</span>
                    </div> */}
                    <div className="stat-item">
                      <span className="stat-number">{stats.totalPeople}</span>
                      <span className="stat-label">Gesamtpersonen</span>
                    </div>
                  </div>
                )}
                
                <div className="votes-table">
                  <h3>Alle Abstimmungen</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Vorname</th>
                        <th>Nachname</th>
                        <th>Status</th>
                        {/* <th>Zusätzliche Gäste</th>
                        <th>Gästenamen</th> */}
                        <th>Zeitpunkt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {votes.map(vote => {
                        const nameParts = vote.name.split(' ');
                        const firstName = nameParts[0] || '';
                        const lastName = nameParts.slice(1).join(' ') || '';
                        return (
                        <tr key={vote.id}>
                          <td>{firstName}</td>
                          <td>{lastName}</td>
                          <td>
                            <span className={`status-badge ${vote.is_attending ? 'attending' : 'not-attending'}`}>
                              {vote.is_attending ? '✅ Dabei' : '❌ Nicht dabei'}
                            </span>
                          </td>
                          {/* <td>{vote.guests_count}</td>
                          <td>{vote.guest_names || '-'}</td> */}
                          <td>{new Date(vote.created_at).toLocaleString('de-DE')}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {votes.length === 0 && (
                    <p>Keine Abstimmungen gefunden</p>
                  )}
                </div>
                
                <button 
                  type="button" 
                  onClick={() => setShowAdmin(false)}
                  className="back-btn"
                >
                  Zurück zur Abstimmung
                </button>
              </div>
            )}
          </div>
        ) : (
          // Normale Abstimmungsseite
          <>
            <div className="admin-toggle">
              <button 
                type="button" 
                onClick={() => setShowAdmin(true)}
                className="admin-btn"
              >
                🔐 Admin
              </button>
            </div>

            <main className="main">
              <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#ff0000' }}>
                Bitte jeden Gast einzeln an- oder abmelden
              </h3>
              <form onSubmit={handleSubmit} className="vote-form">
                <div className="form-group">
                  <label htmlFor="firstName">Vorname *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    placeholder="Dein Vorname"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Nachname *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    placeholder="Dein Nachname"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="radio-group">
                    <input
                      type="radio"
                      name="attendance"
                      checked={formData.isAttending}
                      onChange={() => setFormData(prev => ({ ...prev, isAttending: true }))}
                    />
                    <span className="radio-label">Ja, ich bin dabei!</span>
                  </label>
                  <label className="radio-group">
                    <input
                      type="radio"
                      name="attendance"
                      checked={!formData.isAttending}
                      onChange={() => setFormData(prev => ({ ...prev, isAttending: false }))}
                    />
                    <span className="radio-label">Nein, ich kann nicht kommen</span>
                  </label>
                </div>

                {/* DEAKTIVIERT: Zusätzliche Gäste-Funktion */}
                {/* {formData.isAttending && (
                  <>
                    <div className="form-group">
                      <label htmlFor="guestsCount">Wie viele **zusätzliche** Personen bringst du mit?</label>
                      <select
                        id="guestsCount"
                        value={formData.guestsCount}
                        onChange={(e) => handleGuestsCountChange(Number(e.target.value))}
                      >
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>

                    {formData.guestsCount > 0 && (
                      <div className="form-group">
                        <label>Namen der Gäste</label>
                        {Array.from({ length: formData.guestsCount }, (_, i) => (
                          <div key={i} className="guest-inputs">
                            <input
                              type="text"
                              value={formData.guestNames[i]?.firstName || ''}
                              onChange={(e) => handleGuestNameChange(i, 'firstName', e.target.value)}
                              placeholder={`Gast ${i + 1} Vorname`}
                              required
                            />
                            <input
                              type="text"
                              value={formData.guestNames[i]?.lastName || ''}
                              onChange={(e) => handleGuestNameChange(i, 'lastName', e.target.value)}
                              placeholder={`Gast ${i + 1} Nachname`}
                              required
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )} */}

                {error && <div className="error">{error}</div>}

                <button type="submit" disabled={loading} className="submit-btn">
                  {loading ? 'Wird gesendet...' : 'Absenden'}
                </button>
              </form>
            </main>
          </>
        )}
      </div>
    </div>
  );
}

export default App;
